/**
 * Marca de Fermin para la pantalla de sede (design-system §8.2): isotipo (carpa coral con mástil
 * marino, mismos trazos que `public/favicon.svg`) más el wordmark "FERMIN" en Nunito 800 Marino.
 *
 * - `lg`: standby y pantallas de estado — alto 8 % del lado menor de la pantalla (`fm-brand-lg`).
 * - `md`: cabecera del formulario de activación (48 px). `sm`: 32 px.
 * - Es una imagen (`role="img"`) con nombre accesible; `wordmark={false}` deja solo el isotipo.
 */
import { t } from '../../i18n';

import { cn } from './cn';

export type BrandSize = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<BrandSize, string> = {
  sm: 'h-8',
  md: 'h-12',
  lg: 'fm-brand-lg',
};

const WORDMARK_CLASSES: Record<BrandSize, string> = {
  sm: 'text-fm-lg',
  md: 'text-fm-2xl',
  lg: 'fm-brand-wordmark-lg',
};

export interface BrandProps {
  size?: BrandSize;
  /** Muestra el wordmark "FERMIN" junto al isotipo (por defecto sí). */
  wordmark?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function Brand({
  size = 'md',
  wordmark = true,
  className,
  'data-testid': testId,
}: BrandProps) {
  return (
    <div
      role="img"
      aria-label={t('common.app.name')}
      data-testid={testId}
      className={cn('inline-flex items-center gap-fm-3 select-none', SIZE_CLASSES[size], className)}
    >
      <svg viewBox="0 0 100 100" aria-hidden="true" focusable="false" className="h-full w-auto">
        <path d="M50,72.3333 L18,72.3333 A32,32 0 0 1 24.1147,53.5268 Z" fill="#F79A8B" />
        <path d="M50,72.3333 L24.1147,53.5268 A32,32 0 0 1 40.1147,41.8948 Z" fill="#F05A46" />
        <path d="M50,72.3333 L40.1147,41.8948 A32,32 0 0 1 59.8853,41.8948 Z" fill="#D9483A" />
        <path d="M50,72.3333 L59.8853,41.8948 A32,32 0 0 1 75.8853,53.5268 Z" fill="#F05A46" />
        <path d="M50,72.3333 L75.8853,53.5268 A32,32 0 0 1 82,72.3333 Z" fill="#F79A8B" />
        <path
          d="M18,72.3333 A5.3333,5.3333 0 0 0 28.6667,72.3333 A5.3333,5.3333 0 0 0 39.3333,72.3333 A5.3333,5.3333 0 0 0 50,72.3333 A5.3333,5.3333 0 0 0 60.6667,72.3333 A5.3333,5.3333 0 0 0 71.3333,72.3333 A5.3333,5.3333 0 0 0 82,72.3333 Z"
          fill="#B8382C"
        />
        <line
          x1="50"
          y1="40.3333"
          x2="50"
          y2="22.3333"
          stroke="#1E2D45"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path d="M50,22.3333 L67,27.8333 L50,33.3333 Z" fill="#1E2D45" />
      </svg>
      {wordmark ? (
        <span
          className={cn(
            'font-display font-extrabold tracking-wide text-fm-text uppercase',
            WORDMARK_CLASSES[size],
          )}
        >
          {t('common.app.brand')}
        </span>
      ) : null}
    </div>
  );
}
